// The one reader for a 429 envelope's `details.retry_after_s`.
//
// Extracted 2026-08-13 (E14 UI verification F2) when the change-PASSWORD limiter
// needed the same reader the change-EMAIL one already had. The two mappers phrase
// the wait differently on purpose — change-email names seconds under 90s, the
// password lockout is always whole minutes — but "did the server send a usable
// number at all?" is one question with one answer, and two copies of it would
// drift on the next envelope change.
//
// Every limiter on the platform reports the wait the same way: the `Retry-After`
// header carries it too, but `ApiError` exposes only code/status/details, so
// `details.retry_after_s` is the only form that reaches a mapper.

// One hour, against a widest metered window of 900s: `MemberActionRateLimiter`
// and `ChangeEmailRateLimiter` both hard-code `WINDOW_SECONDS = 900`, and
// `ChangePasswordRateLimiter` takes it from
// `CHANGE_PASSWORD_RATE_LIMIT_WINDOW_SECONDS`, whose shipped default is also 900
// — env-overridable, so that one could legitimately grow, and the headroom is
// for it. ORG-014's cooldown IS measured in hours, and deliberately sends no
// `retry_after_s` at all (`reset_clinician_password/exceptions.py`), so nothing
// legitimate reaches this reader above an hour.
//
// What the ceiling catches: the mistake this field invites is a producer that
// thinks in MILLISECONDS, and every producer is a sliding window whose
// `retry_after_s` is the time until the oldest entry rolls out — routinely tens
// of seconds. Any real wait above ~3.6s sent in ms therefore lands past 3600 and
// is refused. At 86_400 it was not: a 60s wait sent as 60_000 sailed through and
// rendered "in about 1000 minutes" on the forced-password-change screen.
//
// What it does NOT catch: units. A value under the ceiling is returned as sent,
// so a 1s wait encoded as 1000 still renders "in about 17 minutes", and nothing
// here can tell that from a genuine 1000s wait. The clamp bounds how wrong the
// copy can be; it does not validate the unit.
const MAX_PLAUSIBLE_RETRY_AFTER_S = 3_600;

/**
 * `details.retry_after_s` when the server sent a usable one, else null.
 *
 * Null — not 0, not a default — is the "say it without a duration" signal: a
 * missing, non-numeric, non-finite, non-positive or implausibly large value all
 * mean the same thing to the copy, which is that naming a wait would be
 * inventing one.
 */
export function readRetryAfterSeconds(details: unknown): number | null {
  if (!details || typeof details !== "object") return null;
  const value = (details as { retry_after_s?: unknown }).retry_after_s;
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null;
  if (value > MAX_PLAUSIBLE_RETRY_AFTER_S) return null;
  return value;
}

/**
 * A wait in whole minutes, always rounded UP: a sliding window has no
 * second-level precision to promise, and rounding DOWN would send the user back
 * before the budget refilled — a second refusal, and a message that lied.
 */
export function formatWaitMinutes(seconds: number): string {
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? "1 minute" : `${minutes} minutes`;
}
